import { PiralPlugin } from 'piral'

// Matomo Tracker class
class MatomoTracker {
  readonly urlBase: string
  readonly siteId: string | number
  readonly trackerUrl: string
  readonly disabled: boolean
  readonly linkTracking: boolean
  private scriptLoaded: boolean = false

  constructor(config: MatomoConfig) {
    this.urlBase = config.urlBase
    this.siteId = config.siteId
    this.trackerUrl = config.trackerUrl
    this.disabled = config.disabled || false
    this.linkTracking = config.linkTracking !== false

    if (!this.disabled) {
      this.initializeMatomo()
    }
  }

  private initializeMatomo() {
    // Initialize _paq array
    globalThis._paq = globalThis._paq || []

    // Configure tracking
    if (this.linkTracking) {
      globalThis._paq.push(['enableLinkTracking'])
    }

    // Load Matomo script
    const u = this.urlBase.endsWith('/') ? this.urlBase : this.urlBase + '/'
    globalThis._paq.push(['setTrackerUrl', this.trackerUrl], ['setSiteId', this.siteId])

    const d = document
    const g = d.createElement('script')
    const s = d.getElementsByTagName('script')[0]

    g.type = 'text/javascript'
    g.async = true
    g.src = u + 'matomo.js'

    g.onload = () => {
      this.scriptLoaded = true
    }

    s.parentNode?.insertBefore(g, s)
  }

  trackPageView(params?: { documentTitle?: string; href?: string }) {
    if (this.disabled) return

    if (params?.documentTitle) {
      globalThis._paq?.push(['setDocumentTitle', params.documentTitle])
    }
    if (params?.href) {
      globalThis._paq?.push(['setCustomUrl', params.href])
    } else {
      globalThis._paq?.push(['setCustomUrl', globalThis.location.href])
    }
    globalThis._paq?.push(['trackPageView'])
  }

  trackEvent(params: { category: string; action: string; name?: string; value?: number }) {
    if (this.disabled) return
    globalThis._paq?.push(['trackEvent', params.category, params.action, params.name, params.value])
  }

  isDisabled(): boolean {
    return this.disabled
  }

  isScriptLoaded(): boolean {
    return this.scriptLoaded
  }
}

// Configuration interface
interface MatomoConfig {
  urlBase: string
  siteId: string | number
  trackerUrl: string
  disabled?: boolean
  linkTracking?: boolean
}

// Extend Window interface for _paq
declare global {
  interface Global {
    _paq?: any[]
    _env_?: {
      MATOMO_URL?: string
      MATOMO_SITE_ID?: string | number
      MATOMO_ENABLED?: string
    }
  }
}

// Create Matomo instance
function createInstance(config: MatomoConfig): MatomoTracker {
  return new MatomoTracker(config)
}

// Piral Plugin API
export interface TrackingApi {
  useMatomo(): MatomoTracker
}

// Create and export the Piral plugin
const matomoUrl: string = globalThis._env_?.MATOMO_URL || ''

let currentMatomoInstance: MatomoTracker | null = null

export function registerMatomoInstance(): void {
  currentMatomoInstance = createInstance({
    urlBase: matomoUrl + '/',
    siteId: globalThis._env_.MATOMO_SITE_ID,
    trackerUrl: matomoUrl + '/matomo.php',
    disabled: globalThis._env_.MATOMO_ENABLED?.toLowerCase()?.trim() !== 'true',
    linkTracking: false
  })
}

export function createPbaTrackingApi(): PiralPlugin<TrackingApi> {
  return () => ({
    useMatomo: () => currentMatomoInstance
  })
}

export type { MatomoConfig }
